import { useState, useCallback } from 'react';
import { GameState, Player, Role, GamePhase, VoiceType, NightEvent, NarrativeTone } from '@/types/game';

const generateId = () => Math.random().toString(36).substring(2, 9);

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const assignRoles = (playerCount: number): Role[] => {
  const roles: Role[] = [];
  
  // Special roles first
  roles.push('seer');
  
  // Add witch for 6+ players
  if (playerCount >= 6) {
    roles.push('witch');
  }
  
  // Add hunter for 7+ players
  if (playerCount >= 7) {
    roles.push('hunter');
  }
  
  // Add cupid for 8+ players
  if (playerCount >= 8) {
    roles.push('cupid');
  }
  
  // Werewolves: 1 for 5-6 players, 2 for 7-8, 3 for 9-10
  const werewolfCount = playerCount <= 6 ? 1 : playerCount <= 8 ? 2 : 3;
  for (let i = 0; i < werewolfCount; i++) {
    roles.push('werewolf');
  }
  
  // Rest are villagers
  while (roles.length < playerCount) {
    roles.push('villager');
  }
  
  return shuffleArray(roles);
};

export const useGameState = () => {
  const [gameState, setGameState] = useState<GameState | null>(null);

  const createGame = useCallback((playerNames: string[], voiceType: VoiceType, narrativeTone: NarrativeTone = 'taquin') => {
    const roles = assignRoles(playerNames.length);
    const players: Player[] = playerNames.map((name, index) => ({
      id: generateId(),
      name,
      role: roles[index],
      isAlive: true,
      isLover: false,
    }));

    const newGame: GameState = {
      id: generateId(),
      phase: 'lobby',
      players,
      currentTurn: 1,
      voiceType,
      narrativeTone,
      witchPotions: {
        hasHealPotion: true,
        hasKillPotion: true,
      },
      loversChosen: false,
      nightEvents: [],
    };

    setGameState(newGame);
    return newGame;
  }, []);

  const setPhase = useCallback((phase: GamePhase) => {
    setGameState(prev => prev ? { ...prev, phase } : null);
  }, []);

  const startNight = useCallback(() => {
    setGameState(prev => prev ? { 
      ...prev, 
      phase: 'night',
      nightAction: {},
      eliminatedTonight: undefined,
      hunterRevengeTarget: undefined,
    } : null);
  }, []);

  const werewolfVote = useCallback((targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      const target = prev.players.find(p => p.id === targetId);
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'werewolf_kill',
        targetId,
        targetRole: target?.role,
      };
      return {
        ...prev,
        nightAction: { ...prev.nightAction, werewolfTarget: targetId },
        nightEvents: [...prev.nightEvents, newEvent],
      };
    });
  }, []);

  const seerReveal = useCallback((targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      const target = prev.players.find(p => p.id === targetId);
      const seer = prev.players.find(p => p.role === 'seer');
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'seer_reveal',
        actorId: seer?.id,
        targetId,
        targetRole: target?.role,
      };
      return {
        ...prev,
        nightAction: { ...prev.nightAction, seerTarget: targetId },
        nightEvents: [...prev.nightEvents, newEvent],
      };
    });
  }, []);

  const witchHeal = useCallback(() => {
    setGameState(prev => {
      if (!prev) return null;
      const witch = prev.players.find(p => p.role === 'witch');
      const savedPlayer = prev.players.find(p => p.id === prev.nightAction?.werewolfTarget);
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'witch_save',
        actorId: witch?.id,
        targetId: prev.nightAction?.werewolfTarget,
        targetRole: savedPlayer?.role,
        saved: true,
      };
      return {
        ...prev,
        nightAction: { ...prev.nightAction, witchHealUsed: true },
        witchPotions: { ...prev.witchPotions, hasHealPotion: false },
        nightEvents: [...prev.nightEvents, newEvent],
      };
    });
  }, []);

  const witchKill = useCallback((targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      const witch = prev.players.find(p => p.role === 'witch');
      const target = prev.players.find(p => p.id === targetId);
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'witch_kill',
        actorId: witch?.id,
        targetId,
        targetRole: target?.role,
      };
      return {
        ...prev,
        nightAction: { ...prev.nightAction, witchKillTarget: targetId },
        witchPotions: { ...prev.witchPotions, hasKillPotion: false },
        nightEvents: [...prev.nightEvents, newEvent],
      };
    });
  }, []);

  const cupidChooseLovers = useCallback((lover1Id: string, lover2Id: string) => {
    setGameState(prev => {
      if (!prev) return null;
      
      const updatedPlayers = prev.players.map(p => {
        if (p.id === lover1Id) {
          return { ...p, isLover: true, loverId: lover2Id };
        }
        if (p.id === lover2Id) {
          return { ...p, isLover: true, loverId: lover1Id };
        }
        return p;
      });

      const cupid = prev.players.find(p => p.role === 'cupid');
      const newEvent: NightEvent = {
        turn: prev.currentTurn,
        type: 'cupid_lovers',
        actorId: cupid?.id,
        targetId: lover1Id,
        secondTargetId: lover2Id,
      };

      return {
        ...prev,
        players: updatedPlayers,
        nightAction: { ...prev.nightAction, cupidLovers: [lover1Id, lover2Id] },
        loversChosen: true,
        nightEvents: [...prev.nightEvents, newEvent],
      };
    });
  }, []);

  const hunterRevenge = useCallback((targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      
      const hunter = prev.players.find(p => p.role === 'hunter');
      const target = prev.players.find(p => p.id === targetId);
      
      let updatedPlayers = prev.players.map(p => 
        p.id === targetId ? { ...p, isAlive: false } : p
      );

      const newEvents: NightEvent[] = [{
        turn: prev.currentTurn,
        type: 'hunter_revenge',
        actorId: hunter?.id,
        targetId,
        targetRole: target?.role,
      }];

      // Check if hunter killed a lover
      const killedPlayer = updatedPlayers.find(p => p.id === targetId);
      if (killedPlayer?.isLover && killedPlayer.loverId) {
        const lover = prev.players.find(p => p.id === killedPlayer.loverId);
        updatedPlayers = updatedPlayers.map(p =>
          p.id === killedPlayer.loverId ? { ...p, isAlive: false } : p
        );
        newEvents.push({
          turn: prev.currentTurn,
          type: 'lover_death',
          targetId: killedPlayer.loverId,
          targetRole: lover?.role,
        });
      }

      // Check win conditions
      const alivePlayers = updatedPlayers.filter(p => p.isAlive);
      const aliveWerewolves = alivePlayers.filter(p => p.role === 'werewolf');
      const aliveVillagers = alivePlayers.filter(p => p.role !== 'werewolf');
      const aliveLovers = alivePlayers.filter(p => p.isLover);

      let winner: 'werewolves' | 'villagers' | 'lovers' | undefined;
      
      // Check if lovers won (only lovers survive and one is werewolf)
      if (aliveLovers.length === 2 && alivePlayers.length === 2) {
        const loversRoles = aliveLovers.map(l => l.role);
        if (loversRoles.includes('werewolf') && loversRoles.some(r => r !== 'werewolf')) {
          winner = 'lovers';
        }
      }
      
      if (!winner) {
        if (aliveWerewolves.length === 0) {
          winner = 'villagers';
        } else if (aliveWerewolves.length >= aliveVillagers.length) {
          winner = 'werewolves';
        }
      }

      return {
        ...prev,
        phase: winner ? 'end' : 'night',
        players: updatedPlayers,
        hunterRevengeTarget: targetId,
        winner,
        nightEvents: [...prev.nightEvents, ...newEvents],
      };
    });
  }, []);

  const killPlayer = useCallback((playerId: string): { hunterDied: boolean; loversAffected: string[] } => {
    let hunterDied = false;
    let loversAffected: string[] = [];

    setGameState(prev => {
      if (!prev) return null;

      let updatedPlayers = prev.players.map(p => 
        p.id === playerId ? { ...p, isAlive: false } : p
      );

      const killedPlayer = updatedPlayers.find(p => p.id === playerId);
      
      // Check if hunter died
      if (killedPlayer?.role === 'hunter') {
        hunterDied = true;
      }

      // Check if a lover died
      if (killedPlayer?.isLover && killedPlayer.loverId) {
        loversAffected = [playerId, killedPlayer.loverId];
        updatedPlayers = updatedPlayers.map(p =>
          p.id === killedPlayer.loverId ? { ...p, isAlive: false } : p
        );
        
        // Check if the other lover is the hunter
        const otherLover = updatedPlayers.find(p => p.id === killedPlayer.loverId);
        if (otherLover?.role === 'hunter') {
          hunterDied = true;
        }
      }

      return { ...prev, players: updatedPlayers };
    });

    return { hunterDied, loversAffected };
  }, []);

  const startDay = useCallback(() => {
    setGameState(prev => {
      if (!prev) return null;
      
      // Determine who dies tonight
      let eliminatedId = prev.nightAction?.werewolfTarget;
      
      // Witch can save the werewolf target
      if (prev.nightAction?.witchHealUsed && eliminatedId) {
        eliminatedId = undefined;
      }

      // Process deaths
      let updatedPlayers = prev.players;
      const deathIds: string[] = [];
      
      // Werewolf victim (if not saved by witch)
      if (eliminatedId) {
        deathIds.push(eliminatedId);
      }
      
      // Witch kill target
      if (prev.nightAction?.witchKillTarget) {
        deathIds.push(prev.nightAction.witchKillTarget);
      }

      // Apply deaths and check for lovers
      let hunterDied = false;
      const processedDeaths = new Set<string>();
      
      deathIds.forEach(id => {
        if (processedDeaths.has(id)) return;
        processedDeaths.add(id);
        
        const player = prev.players.find(p => p.id === id);
        if (player?.isAlive) {
          updatedPlayers = updatedPlayers.map(p => 
            p.id === id ? { ...p, isAlive: false } : p
          );
          
          if (player.role === 'hunter') {
            hunterDied = true;
          }
          
          // If lover dies, other lover dies too
          if (player.isLover && player.loverId) {
            const lover = prev.players.find(p => p.id === player.loverId);
            if (lover?.isAlive && !processedDeaths.has(lover.id)) {
              processedDeaths.add(lover.id);
              updatedPlayers = updatedPlayers.map(p => 
                p.id === lover.id ? { ...p, isAlive: false } : p
              );
              if (lover.role === 'hunter') {
                hunterDied = true;
              }
            }
          }
        }
      });

      // Check win conditions after night kill
      const alivePlayers = updatedPlayers.filter(p => p.isAlive);
      const aliveWerewolves = alivePlayers.filter(p => p.role === 'werewolf');
      const aliveVillagers = alivePlayers.filter(p => p.role !== 'werewolf');
      const aliveLovers = alivePlayers.filter(p => p.isLover);

      let winner: 'werewolves' | 'villagers' | 'lovers' | undefined;
      
      // Check lovers victory
      if (aliveLovers.length === 2 && alivePlayers.length === 2) {
        const loversRoles = aliveLovers.map(l => l.role);
        if (loversRoles.includes('werewolf') && loversRoles.some(r => r !== 'werewolf')) {
          winner = 'lovers';
        }
      }
      
      if (!winner) {
        if (aliveWerewolves.length === 0) {
          winner = 'villagers';
        } else if (aliveWerewolves.length >= aliveVillagers.length) {
          winner = 'werewolves';
        }
      }

      // If hunter died and no winner yet, go to hunter revenge phase
      const nextPhase = hunterDied && !winner ? 'hunter-revenge' : (winner ? 'end' : 'day');

      return {
        ...prev,
        phase: nextPhase,
        players: updatedPlayers,
        eliminatedTonight: eliminatedId,
        winner,
      };
    });
  }, []);

  const castVote = useCallback((voterId: string, targetId: string) => {
    setGameState(prev => {
      if (!prev) return null;
      return {
        ...prev,
        players: prev.players.map(p => 
          p.id === voterId ? { ...p, votedFor: targetId } : p
        ),
      };
    });
  }, []);

  const executeVote = useCallback(() => {
    setGameState(prev => {
      if (!prev) return null;

      // Count votes
      const voteCount: Record<string, number> = {};
      prev.players.filter(p => p.isAlive && p.votedFor).forEach(p => {
        if (p.votedFor) {
          voteCount[p.votedFor] = (voteCount[p.votedFor] || 0) + 1;
        }
      });

      // Find player with most votes
      let maxVotes = 0;
      let eliminatedId: string | undefined;
      Object.entries(voteCount).forEach(([id, count]) => {
        if (count > maxVotes) {
          maxVotes = count;
          eliminatedId = id;
        }
      });

      let updatedPlayers = prev.players.map(p => ({
        ...p,
        isAlive: p.id === eliminatedId ? false : p.isAlive,
        votedFor: undefined,
      }));

      let hunterDied = false;
      const eliminatedPlayer = prev.players.find(p => p.id === eliminatedId);
      
      if (eliminatedPlayer?.role === 'hunter') {
        hunterDied = true;
      }

      // If eliminated player is a lover, kill their partner too
      if (eliminatedPlayer?.isLover && eliminatedPlayer.loverId) {
        const lover = updatedPlayers.find(p => p.id === eliminatedPlayer.loverId);
        if (lover?.isAlive) {
          updatedPlayers = updatedPlayers.map(p =>
            p.id === eliminatedPlayer.loverId ? { ...p, isAlive: false } : p
          );
          if (lover.role === 'hunter') {
            hunterDied = true;
          }
        }
      }

      // Check win conditions
      const alivePlayers = updatedPlayers.filter(p => p.isAlive);
      const aliveWerewolves = alivePlayers.filter(p => p.role === 'werewolf');
      const aliveVillagers = alivePlayers.filter(p => p.role !== 'werewolf');
      const aliveLovers = alivePlayers.filter(p => p.isLover);

      let winner: 'werewolves' | 'villagers' | 'lovers' | undefined;
      
      if (aliveLovers.length === 2 && alivePlayers.length === 2) {
        const loversRoles = aliveLovers.map(l => l.role);
        if (loversRoles.includes('werewolf') && loversRoles.some(r => r !== 'werewolf')) {
          winner = 'lovers';
        }
      }
      
      if (!winner) {
        if (aliveWerewolves.length === 0) {
          winner = 'villagers';
        } else if (aliveWerewolves.length >= aliveVillagers.length) {
          winner = 'werewolves';
        }
      }

      // If hunter died and no winner, go to hunter revenge
      const nextPhase = hunterDied && !winner ? 'hunter-revenge' : 'elimination';

      return {
        ...prev,
        phase: nextPhase,
        players: updatedPlayers,
        eliminatedTonight: eliminatedId,
        winner,
        currentTurn: prev.currentTurn + 1,
      };
    });
  }, []);

  const getAlivePlayers = useCallback(() => {
    return gameState?.players.filter(p => p.isAlive) || [];
  }, [gameState]);

  const getPlayer = useCallback((id: string) => {
    return gameState?.players.find(p => p.id === id);
  }, [gameState]);

  const resetGame = useCallback(() => {
    setGameState(null);
  }, []);

  return {
    gameState,
    createGame,
    setPhase,
    startNight,
    startDay,
    werewolfVote,
    seerReveal,
    witchHeal,
    witchKill,
    cupidChooseLovers,
    hunterRevenge,
    killPlayer,
    castVote,
    executeVote,
    getAlivePlayers,
    getPlayer,
    resetGame,
  };
};
